import { createSignal, For, JSX, Match, Show, Switch } from 'solid-js';
import { useQuery } from '@tanstack/solid-query';
import type {
  DeploymentManifest,
  FeatureDeployment,
} from './FeatureDeploymentsPage.types';

const JIRA_BASE = 'https://jira.trimble.tools/browse/';
const GITHUB_REPO = 'https://github.com/Trimble-Connect/trimble-web-apps';
const MANIFEST_URL = '/feature/index.json';

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_DAY = 86400;
const HTTP_NOT_FOUND = 404;
const COMMIT_SHORT_LENGTH = 8;

interface DeploymentEntry {
  readonly slug: string;
  readonly data: FeatureDeployment;
}

function timeAgo(dateStr: string): string {
  const seconds = Math.floor(
    (Date.now() - new Date(dateStr).getTime()) / MS_PER_SECOND
  );
  if (seconds < SECONDS_PER_MINUTE) return 'just now';
  if (seconds < SECONDS_PER_HOUR)
    return `${Math.floor(seconds / SECONDS_PER_MINUTE)}m ago`;
  if (seconds < SECONDS_PER_DAY)
    return `${Math.floor(seconds / SECONDS_PER_HOUR)}h ago`;
  return `${Math.floor(seconds / SECONDS_PER_DAY)}d ago`;
}

function extractJiraId(slug: string): string | null {
  // eslint-disable-next-line sonarjs/super-linear-regex -- slugs are short branch names from our own manifest
  const match = /([A-Z]+-\d+)/.exec(slug);
  return match ? match[1] : null;
}

async function fetchManifest(): Promise<DeploymentEntry[]> {
  const res = await fetch(`${MANIFEST_URL}?t=${Date.now()}`);

  if (!res.ok) {
    if (res.status === HTTP_NOT_FOUND) return [];
    throw new Error(`HTTP ${res.status}`);
  }

  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    return [];
  }

  const manifest: DeploymentManifest = await res.json();

  return Object.entries(manifest)
    .map(([slug, data]) => ({ slug, data }))
    .sort(
      (a, b) =>
        new Date(b.data.deployedAt).getTime() -
        new Date(a.data.deployedAt).getTime()
    );
}

function DeploymentCard(props: {
  readonly entry: DeploymentEntry;
}): JSX.Element {
  const jiraId = (): string | null => extractJiraId(props.entry.slug);
  const commitShort = (): string =>
    (props.entry.data.commit ?? '').substring(0, COMMIT_SHORT_LENGTH);

  return (
    <div class="bg-white border border-gray-2 rounded-lg px-6 py-5 flex items-center gap-5 transition-shadow hover:border-blue hover:shadow-md max-sm:flex-col max-sm:items-start">
      <div class="flex-1 min-w-0">
        <div class="text-base font-semibold text-blue-dark break-all">
          {props.entry.slug}
        </div>
        <div class="text-xs text-gray-5 mt-1 font-mono">
          {props.entry.data.branch}
        </div>
        <div class="flex gap-4 mt-2 text-xs text-gray-4 flex-wrap">
          <Show when={jiraId()}>
            <a
              href={`${JIRA_BASE}${jiraId()}`}
              target="_blank"
              rel="noopener noreferrer"
              class="text-blue-600 hover:underline"
            >
              {jiraId()}
            </a>
          </Show>
          <Show when={props.entry.data.actor}>
            <span>by {props.entry.data.actor}</span>
          </Show>
          <Show when={props.entry.data.deployedAt}>
            <span>{timeAgo(props.entry.data.deployedAt)}</span>
          </Show>
          <Show when={commitShort()}>
            <a
              href={`${GITHUB_REPO}/commit/${props.entry.data.commit}`}
              target="_blank"
              rel="noopener noreferrer"
              class="text-blue-600 hover:underline font-mono"
            >
              {commitShort()}
            </a>
          </Show>
        </div>
      </div>
      <div class="flex gap-2 shrink-0 max-sm:w-full">
        <a
          href={props.entry.data.url || `/feature/${props.entry.slug}/`}
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium bg-blue text-white hover:bg-blue-defaultDark no-underline transition-colors max-sm:flex-1 max-sm:justify-center"
        >
          Open
        </a>
      </div>
    </div>
  );
}

export function FeatureDeploymentsPage(): JSX.Element {
  const query = useQuery(() => ({
    queryKey: ['feature-deployments'],
    queryFn: fetchManifest,
    staleTime: 0,
    // Mobile mapping's client turns refetchOnMount off; this list should be fresh on every visit.
    refetchOnMount: true,
    retry: false,
  }));
  const [refreshing, setRefreshing] = createSignal(false);

  const handleRefresh = async (): Promise<void> => {
    setRefreshing(true);
    try {
      await query.refetch();
    } finally {
      setRefreshing(false);
    }
  };

  const count = (): number => query.data?.length ?? 0;

  return (
    <div class="h-full flex flex-col overflow-y-auto bg-gray-0">
      <div class="bg-blue-dark text-white px-8 py-5 flex items-center gap-4 sticky top-0 z-10">
        <h1 class="text-xl font-semibold">Feature Deployments</h1>
        <Show when={!query.isPending}>
          <span class="bg-white/15 px-3 py-1 rounded-xl text-xs">
            {count()} deployment{count() !== 1 ? 's' : ''}
          </span>
        </Show>
        <button
          class="ml-auto bg-transparent border border-white/30 text-white px-3.5 py-1.5 rounded-md text-xs cursor-pointer hover:bg-white/10 transition-colors disabled:opacity-50"
          onClick={handleRefresh}
          disabled={refreshing()}
        >
          {refreshing() ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      <div class="max-w-[960px] mx-auto w-full px-6 py-8">
        <Switch>
          <Match when={query.isPending}>
            <div class="text-center py-16 text-gray-4">
              Loading deployments...
            </div>
          </Match>
          <Match when={query.isError}>
            <div class="text-center py-16 text-red-600">
              Failed to load deployments:{' '}
              {query.error instanceof Error
                ? query.error.message
                : String(query.error)}
            </div>
          </Match>
          <Match when={count() === 0}>
            <div class="text-center py-20 text-gray-4">
              <p class="text-base">
                No feature branches are currently deployed.
              </p>
            </div>
          </Match>
          <Match when={count() > 0}>
            <div class="flex flex-col gap-3">
              <For each={query.data}>
                {(entry) => <DeploymentCard entry={entry} />}
              </For>
            </div>
          </Match>
        </Switch>
      </div>
    </div>
  );
}
